import { useCallback, useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Loader2,
  X,
  ChevronDown,
  ExternalLink,
  AlertCircle,
  Youtube,
  ArrowUp,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  detectPlatform,
  extractYoutubeId,
  type ExtractedPlace,
  type InspirationCategory,
  type InspirationFeed,
  type InspirationVideo,
} from "@/lib/inspiration";
import { buildClickUrl } from "@/lib/clicks";
import {
  trackInspirationOpened,
  trackInspirationVideoExpanded,
  trackInspirationPasteUsed,
} from "@/lib/analytics";

// Minimal day shape the picker needs (id + date + title + index label).
export interface InspirationDayOption {
  id: string;
  n: number; // 1-based day number, shown in the picker and in "Add to Day N"
  title: string; // the day's own title, e.g. "Alfama & azulejos"
  label: string; // e.g. "Day 2 · Fri, Jun 19"
  date: string; // ISO, passed to the concierge so it targets the right day
}

interface InspirationProps {
  destination: string;
  // Called when the user clicks "Add to trip" on an extracted place.
  // targetDate is the ISO date of the chosen day, or null = let AI decide.
  onAddPlace: (args: {
    place: ExtractedPlace;
    video: InspirationVideo;
    targetDate: string | null;
  }) => void;
  // Days the user can target. Empty until the trip has days.
  days?: InspirationDayOption[];
  // The day the user is currently reading. It seeds each row's "Add to
  // Day N" so the default target is the day in front of them.
  activeDayNumber?: number;
  // Total videos on offer, including a pasted one. The trip page shows this
  // beside the Inspiration tab and in the mobile entry row and sheet header,
  // so it has to come back out of here. null means "not known yet": until
  // the feed has actually loaded there is no honest number to show.
  onVideoCountChange?: (count: number | null) => void;
  // True while the concierge is streaming: the pre-check waits, since both
  // draw on the same Anthropic budget and the build comes first.
  deferPrefetch?: boolean;
  // Used for analytics: lets us tag video-open clicks with the trip
  // they came from. Optional — works fine without (logged as anon).
  tripId?: string;
}

// Internal add handler: the place row fires (place, video, targetDate);
// the parent (Inspiration.handleAdd) dedupes and forwards to onAddPlace.
type InternalAddHandler = (args: {
  place: ExtractedPlace;
  video: InspirationVideo;
  targetDate: string | null;
}) => void;

type ExtractionState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; places: ExtractedPlace[] }
  | { status: "error"; message: string };

// The paste-a-link entry row has four states. "failed" is the request
// coming back fine with nothing in it, which is a correct answer and not
// an error; "error" is the ones where something actually went wrong.
type PasteState =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "failed" }
  | { status: "error"; message: string };

// A successfully analyzed link. It becomes a "Pasted" group at the top of
// the feed, so it outlives the entry row's own state.
interface PastedVideo {
  video: InspirationVideo;
  places: ExtractedPlace[];
}

const PASTE_TRANSPORT_ERROR =
  "Something went wrong reading that video. Try again in a moment.";

// A video YouTube cannot find: deleted, private, or a mistyped id. Retrying
// will never change the answer, so it must not read like a hiccup.
const PASTE_NOT_FOUND_ERROR =
  "We could not find that video on YouTube. Double-check the link.";

const youtubeWatchUrl = (videoId: string) =>
  `https://www.youtube.com/watch?v=${videoId}`;

const youtubeEmbedUrl = (videoId: string) =>
  `https://www.youtube.com/embed/${videoId}`;

// Stable key for tracking which (video, place) pairs the user has clicked
// Add on. Case- and whitespace-tolerant since the same place can appear
// in multiple videos with slightly different capitalizations.
// Pre-check results remembered for this page load, per destination and
// video, so switching tabs or reopening the sheet never asks the server
// again for a video it has already answered.
const prefetchedPlaces = new Map<string, ExtractedPlace[]>();
const prefetchKey = (destination: string, videoId: string) =>
  `${destination}\u0000${videoId}`;

const makeAddKey = (videoId: string, placeName: string) =>
  `${videoId}|${placeName.toLowerCase().trim()}`;

const Inspiration = ({
  destination,
  onAddPlace,
  days = [],
  activeDayNumber,
  onVideoCountChange,
  deferPrefetch = false,
  tripId,
}: InspirationProps) => {
  const [feed, setFeed] = useState<InspirationFeed | null>(null);
  const [feedStatus, setFeedStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const [feedError, setFeedError] = useState<string | null>(null);

  const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);
  const [extractionsByVideoId, setExtractionsByVideoId] = useState<
    Record<string, ExtractionState>
  >({});

  const [pasteInput, setPasteInput] = useState("");
  const [pasteState, setPasteState] = useState<PasteState>({ status: "idle" });
  const [pastedVideo, setPastedVideo] = useState<PastedVideo | null>(null);
  // Track which (video, place) pairs the user has already clicked Add on
  // so the buttons can show "Added" feedback and not double-fire.
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());
  // Per-place verification state, keyed the same way as addedKeys.
  // Nothing may flip straight to Added: a place is only added once Google
  // Places has actually confirmed it.
  const [addingKeys, setAddingKeys] = useState<
    Record<string, "pending" | "failed">
  >({});

  const handleAdd = async (args: {
    place: ExtractedPlace;
    video: InspirationVideo;
    targetDate: string | null;
  }) => {
    const key = makeAddKey(args.video.videoId, args.place.name);
    if (addedKeys.has(key) || addingKeys[key] === "pending") return;
    setAddingKeys((prev) => ({ ...prev, [key]: "pending" }));

    // The concierge's own verify_place runs server-side inside the agent
    // loop and its result never reaches the browser, so we ask the same
    // checker directly and get an answer tied to THIS place.
    let verified = false;
    try {
      const res = await fetch("/api/verify-place", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: args.place.name, near: trimmedDestination }),
      });
      const data = await res.json().catch(() => null);
      verified = res.ok && data?.verified === true;
    } catch {
      verified = false;
    }

    if (!verified) {
      setAddingKeys((prev) => ({ ...prev, [key]: "failed" }));
      return;
    }

    setAddingKeys((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setAddedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    onAddPlace(args);
  };
  // Track in-flight aborts so we don't race state when the user
  // re-pastes or switches destinations.
  const abortRef = useRef<AbortController | null>(null);

  const trimmedDestination = destination.trim();

  // Gated on feedStatus, not just on feed: while a new destination is being
  // fetched the old feed is still in state, and summing it would label the
  // previous destination's videos with the new destination's name.
  const videoCount =
    feedStatus === "loaded" && feed
      ? (feed.categories ?? []).reduce(
          (n, c) => n + (c.videos?.length ?? 0),
          0
        ) + (pastedVideo ? 1 : 0)
      : null;
  useEffect(() => {
    onVideoCountChange?.(videoCount);
  }, [videoCount, onVideoCountChange]);

  // Fetch the feed when destination changes
  useEffect(() => {
    if (!trimmedDestination) {
      setFeed(null);
      setFeedStatus("idle");
      return;
    }
    // Funnel: the Inspiration feature was viewed for a destination (once per
    // destination). Non-identifying: no destination value attached.
    trackInspirationOpened();
    setFeedStatus("loading");
    setFeedError(null);
    // Drop the previous destination's feed so nothing downstream can read it.
    setFeed(null);
    const ctrl = new AbortController();
    fetch(
      `/api/inspiration?destination=${encodeURIComponent(trimmedDestination)}`,
      { signal: ctrl.signal }
    )
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            data?.error || `Inspiration feed failed (${res.status})`
          );
        }
        return data as InspirationFeed;
      })
      .then((data) => {
        setFeed(data);
        setFeedStatus("loaded");
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setFeedError(err?.message || "Couldn't load inspiration.");
        setFeedStatus("error");
      });
    return () => ctrl.abort();
  }, [trimmedDestination]);

  // Reset expansion state when destination changes
  useEffect(() => {
    setExpandedVideoId(null);
    setExtractionsByVideoId({});
    setPastedVideo(null);
    setPasteState({ status: "idle" });
    setPasteInput("");
  }, [trimmedDestination]);

  // Pre-check every video in the feed so each card can say up front whether
  // it names places to add, before anyone taps it. One request per group,
  // Eats first; the results seed extractionsByVideoId, so a tap on a
  // checked card opens without a wait. The server caches per video for a
  // week, so only the first visitor to a destination waits for the checks.
  // Three gates keep it cheap: answers are remembered for this page load
  // (a remount does not refetch), nothing runs while the panel is not
  // actually displayed (the desktop tree is mounted but hidden on phones),
  // and nothing runs while the concierge is streaming.
  const rootRef = useRef<HTMLDivElement>(null);
  const extractionsRef = useRef(extractionsByVideoId);
  extractionsRef.current = extractionsByVideoId;
  useEffect(() => {
    if (feedStatus !== "loaded" || !feed || !trimmedDestination || deferPrefetch) return;
    const destinationAtStart = trimmedDestination;
    const ctrl = new AbortController();
    let cancelled = false;
    let started = false;

    const seed = (entries: Record<string, ExtractedPlace[]>) => {
      if (!Object.keys(entries).length) return;
      setExtractionsByVideoId((prev) => {
        const next = { ...prev };
        for (const [id, places] of Object.entries(entries)) {
          // A tap may have finished first; its answer is the same.
          if (next[id]?.status === "loaded") continue;
          next[id] = { status: "loaded", places };
        }
        return next;
      });
    };

    const run = async () => {
      if (started) return;
      started = true;
      for (const category of feed.categories ?? []) {
        if (cancelled) return;
        const ids: string[] = [];
        const remembered: Record<string, ExtractedPlace[]> = {};
        for (const video of category.videos ?? []) {
          const existing = extractionsRef.current[video.videoId];
          if (existing && existing.status !== "error") continue;
          const known = prefetchedPlaces.get(prefetchKey(destinationAtStart, video.videoId));
          if (known) remembered[video.videoId] = known;
          else ids.push(video.videoId);
        }
        seed(remembered);
        if (!ids.length) continue;
        try {
          const res = await fetch("/api/extract-places-batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ destination: destinationAtStart, videoIds: ids }),
            signal: ctrl.signal,
          });
          if (!res.ok) continue; // the cards stay unknown; a tap still works
          const data = await res.json().catch(() => ({}));
          if (cancelled) return;
          const results = (data?.results ?? {}) as Record<
            string,
            { places?: ExtractedPlace[] } | undefined
          >;
          const answered: Record<string, ExtractedPlace[]> = {};
          for (const id of ids) {
            const places = results[id]?.places;
            if (!Array.isArray(places)) continue;
            prefetchedPlaces.set(prefetchKey(destinationAtStart, id), places);
            answered[id] = places;
          }
          seed(answered);
        } catch (err) {
          if ((err as { name?: string })?.name === "AbortError") return;
          // Leave the group unknown and move on to the next.
        }
      }
    };

    // Displayed means laid out: the desktop tree is mounted but display:none
    // on phones. A ResizeObserver on the panel fires the moment it gets a
    // size (a rotation, a wider window), which a window resize event does
    // not reliably cover under every viewport change.
    const isDisplayed = () =>
      !!rootRef.current && rootRef.current.getClientRects().length > 0;
    let observer: ResizeObserver | null = null;
    const onResize = () => {
      if (!isDisplayed()) return;
      observer?.disconnect();
      window.removeEventListener("resize", onResize);
      void run();
    };
    if (isDisplayed()) {
      void run();
    } else {
      window.addEventListener("resize", onResize);
      if (typeof ResizeObserver !== "undefined" && rootRef.current) {
        observer = new ResizeObserver(onResize);
        observer.observe(rootRef.current);
      }
    }
    return () => {
      cancelled = true;
      ctrl.abort();
      observer?.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [feed, feedStatus, trimmedDestination, deferPrefetch]);

  const runExtraction = useCallback(
    async (video: InspirationVideo) => {
      setExtractionsByVideoId((prev) => ({
        ...prev,
        [video.videoId]: { status: "loading" },
      }));
      try {
        const res = await fetch("/api/extract-places", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            videoId: video.videoId,
            title: video.title,
            description: video.description,
            channelTitle: video.channelTitle,
            destination: trimmedDestination,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            data?.error || `Extraction failed (${res.status})`
          );
        }
        setExtractionsByVideoId((prev) => ({
          ...prev,
          [video.videoId]: {
            status: "loaded",
            places: Array.isArray(data.places) ? data.places : [],
          },
        }));
      } catch (err) {
        // A failed tap must not throw away an answer the pre-check landed
        // in the meantime.
        setExtractionsByVideoId((prev) =>
          prev[video.videoId]?.status === "loaded"
            ? prev
            : {
                ...prev,
                [video.videoId]: {
                  status: "error",
                  message: err instanceof Error ? err.message : "Extraction failed.",
                },
              }
        );
      }
    },
    [trimmedDestination]
  );

  const handleToggleVideo = (video: InspirationVideo) => {
    if (expandedVideoId === video.videoId) {
      setExpandedVideoId(null);
      return;
    }
    trackInspirationVideoExpanded();
    setExpandedVideoId(video.videoId);
    const existing = extractionsByVideoId[video.videoId];
    if (!existing || existing.status === "error") {
      runExtraction(video);
    }
    // Fire-and-forget: log the video open as a click event so it shows
    // up in the /stats activity feed. The trackUrl GET hits our redirect
    // endpoint; we don't actually need the response, just the side
    // effect of incrementing counters + appending to the recent list.
    const trackUrl = buildClickUrl({
      url: `https://www.youtube.com/watch?v=${video.videoId}`,
      tripId,
      itemId: video.videoId,
      kind: "video",
    });
    fetch(trackUrl, { method: "GET", redirect: "manual" }).catch(() => {});
  };

  const handlePasteSubmit = async () => {
    const url = pasteInput.trim();
    if (!url) return;
    trackInspirationPasteUsed();
    const platform = detectPlatform(url);
    if (platform !== "youtube") {
      // Three different mistakes, three different sentences. The old copy
      // promised "that platform support is coming soon" even for a typed
      // channel name, where there is no platform to be coming.
      const label =
        platform === "tiktok"
          ? "TikTok"
          : platform === "instagram"
          ? "Instagram"
          : platform === "xiaohongshu"
          ? "Xiaohongshu"
          : null;
      setPasteState({
        status: "error",
        message: label
          ? `We can only read YouTube links right now. ${label} support is coming soon.`
          : platform === "youtube-other"
          ? "That is a YouTube link, but not to a single video. Open the video itself and copy its link."
          : platform === "none"
          ? "That does not look like a link. Paste the address of a YouTube video."
          : "We can only read YouTube links right now. Paste a link to a YouTube video.",
      });
      return;
    }
    const videoId = extractYoutubeId(url);
    if (!videoId) {
      setPasteState({
        status: "error",
        message: "Couldn't read a video ID from that URL. Double-check the link.",
      });
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setPasteState({ status: "pending" });

    try {
      const res = await fetch("/api/extract-places", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          videoId,
          destination: trimmedDestination,
        }),
        signal: ctrl.signal,
      });
      const data = await res.json().catch(() => ({}));
      // Never surface an HTTP status or a raw server string to the user.
      // Set state and return rather than throw: the catch below rewrites
      // any thrown message as the transport error.
      if (data?.code === "not_found") {
        setPasteState({ status: "error", message: PASTE_NOT_FOUND_ERROR });
        return;
      }
      if (!res.ok) throw new Error(PASTE_TRANSPORT_ERROR);

      const places: ExtractedPlace[] = Array.isArray(data.places)
        ? data.places
        : [];
      // A video whose description names no places is a correct answer, not
      // a failure of ours, so it gets its own line rather than an error.
      if (!places.length) {
        setPasteState({ status: "failed" });
        setPasteInput("");
        return;
      }

      const video: InspirationVideo = {
        videoId,
        title: data.video?.title || "Pasted video",
        description: data.video?.description || "",
        channelTitle: data.video?.channelTitle || "",
        publishedAt: null,
        thumbnail: data.video?.thumbnail || null,
      };
      setPastedVideo({ video, places });
      setPasteState({ status: "idle" });
      setPasteInput("");
      // Open it straight away, and seed the extraction so the expanded card
      // shows the places it just found instead of re-fetching them.
      setExpandedVideoId(videoId);
      setExtractionsByVideoId((prev) => ({
        ...prev,
        [videoId]: { status: "loaded", places },
      }));
    } catch (err) {
      if ((err as any)?.name === "AbortError") return;
      setPasteState({ status: "error", message: PASTE_TRANSPORT_ERROR });
    }
  };

  if (!trimmedDestination) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center bg-muted/20">
        <div className="max-w-xs">
          <Sparkles className="h-6 w-6 mx-auto text-[color-mix(in_srgb,var(--fn-accent-text)_70%,transparent)] mb-3" />
          <p className="text-sm text-ink-muted leading-relaxed">
            Once you set a destination, this tab fills up with what's going
            viral on YouTube about that place right now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="h-full overflow-y-auto"
      data-inspiration-scroll="true"
    >
      {/* Padding per the artboards: 16px 22px 32px on the mobile sheet
          (plus the home-indicator safe area, since the sheet is fixed to
          the screen edge) and 16px 20px at lg, where the desktop panel's
          own 150px bottom padding already clears the docked concierge. */}
      <div className="pt-4 px-[22px] pb-[calc(2rem+env(safe-area-inset-bottom))] space-y-6 lg:px-5 lg:pb-0">
        {/* Paste-a-link block (always first) */}
        <section>
          <div className="flex items-center gap-2 border border-rule rounded-full pl-3.5 pr-1.5 py-1.5 bg-card">
            <span className="text-ink-faint inline-flex shrink-0">
              <Youtube className="h-3.5 w-3.5 lg:h-[15px] lg:w-[15px]" />
            </span>
            <input
              id="paste-video"
              type="url"
              value={pasteInput}
              onChange={(e) => {
                setPasteInput(e.target.value);
                // Typing dismisses whatever the last attempt said.
                setPasteState({ status: "idle" });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handlePasteSubmit();
                }
              }}
              disabled={pasteState.status === "pending"}
              placeholder="https://youtu.be/..."
              aria-label="Paste a YouTube link"
              // 16px on mobile, because iOS Safari zooms the whole page
              // into any field below that on focus and never zooms back
              // out, which cropped the sheet's left and right edges. The
              // artboard's 12.5px mono stays at lg, where there is no zoom.
              className="flex-1 min-w-0 bg-transparent border-none outline-none font-mono text-[16px] lg:text-[12.5px] text-ink placeholder:text-ink-faint disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handlePasteSubmit}
              disabled={!pasteInput.trim() || pasteState.status === "pending"}
              aria-label="Analyze link"
              className="h-11 w-11 -my-[3px] -mr-[3px] lg:h-[30px] lg:w-[30px] lg:m-0 shrink-0 inline-flex items-center justify-center">
              <span className="h-[38px] w-[38px] lg:h-[30px] lg:w-[30px] rounded-full bg-accent text-on-accent hover:bg-accent-hover transition-colors inline-flex items-center justify-center">
                <ArrowUp className="h-[15px] w-[15px] lg:h-[13px] lg:w-[13px]" />
              </span>
            </button>
          </div>

          {/* The three outcome lines. They sit between the pill and the
              handwritten hint, and clear themselves on the next keystroke. */}
          <div aria-live="polite">
            {pasteState.status === "error" && (
              <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-danger">
                <AlertCircle className="h-[13px] w-[13px] mt-0.5 shrink-0" />
                <span>{pasteState.message}</span>
              </div>
            )}
            {pasteState.status === "pending" && (
              <div className="mt-2 flex items-center gap-2.5 hand text-base text-accent-text">
                <svg
                  viewBox="0 0 64 12"
                  fill="none"
                  aria-hidden="true"
                  className="w-16 h-3 shrink-0"
                >
                  <path
                    d="M2 8 C18 2, 40 10, 62 4"
                    stroke="var(--fn-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="2 7"
                    className="animate-dot-walk"
                  />
                </svg>
                reading the description for places...
              </div>
            )}
            {pasteState.status === "failed" && (
              <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-ink-muted">
                <AlertCircle className="h-[13px] w-[13px] mt-0.5 shrink-0" />
                <span>
                  No places found in that video. Try one that names the spots
                  in its description.
                </span>
              </div>
            )}
          </div>

          <p className="hand text-base text-ink-faint mt-2.5">
            paste any YouTube link, or browse creators for {trimmedDestination}
          </p>
        </section>

        {/* A successfully analyzed link becomes a real group at the top of
            the feed, above every category, and opens expanded. It lives
            outside the feed-status block so it survives a feed error. */}
        {pastedVideo && (
          <CategorySection
            category={{
              id: "pasted",
              label: "Pasted",
              emoji: "",
              videos: [pastedVideo.video],
            }}
            destination={trimmedDestination}
            expandedVideoId={expandedVideoId}
            extractionsByVideoId={extractionsByVideoId}
            onToggleVideo={handleToggleVideo}
            onAddPlace={handleAdd}
            addedKeys={addedKeys}
            addingKeys={addingKeys}
            days={days}
            activeDayNumber={activeDayNumber}
          />
        )}

        {/* Feed status */}
        {feedStatus === "loading" && (
          <div className="flex items-center gap-2 text-sm text-ink-muted py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading what's trending in {trimmedDestination}...
          </div>
        )}
        {feedStatus === "error" && (
          <div className="flex items-start gap-2 text-sm text-ink-muted p-3 rounded-xl bg-paper-warm border border-rule">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-danger" />
            <span>{feedError}</span>
          </div>
        )}

        {feedStatus === "loaded" &&
          feed?.categories?.map((cat) => (
            <CategorySection
              key={cat.id}
              category={cat}
              destination={trimmedDestination}
              expandedVideoId={expandedVideoId}
              extractionsByVideoId={extractionsByVideoId}
              onToggleVideo={handleToggleVideo}
              onAddPlace={handleAdd}
              addedKeys={addedKeys}
              addingKeys={addingKeys}
              days={days}
              activeDayNumber={activeDayNumber}
            />
          ))}
      </div>
    </div>
  );
};

interface CategorySectionProps {
  category: InspirationCategory;
  destination: string;
  expandedVideoId: string | null;
  extractionsByVideoId: Record<string, ExtractionState>;
  onToggleVideo: (v: InspirationVideo) => void;
  onAddPlace: InternalAddHandler;
  addedKeys: Set<string>;
  addingKeys: Record<string, "pending" | "failed">;
  days: InspirationDayOption[];
  activeDayNumber?: number;
}

const CategorySection = ({
  category,
  destination,
  expandedVideoId,
  extractionsByVideoId,
  onToggleVideo,
  onAddPlace,
  addedKeys,
  addingKeys,
  days,
  activeDayNumber,
}: CategorySectionProps) => {
  const expandedInThisCategory = category.videos.find(
    (v) => v.videoId === expandedVideoId
  );
  // undefined until a video has been checked; the card badge and the
  // header suffix both read from here.
  const placeCountFor = (videoId: string): number | undefined => {
    const extraction = extractionsByVideoId[videoId];
    return extraction?.status === "loaded" ? extraction.places.length : undefined;
  };
  const videos = category.videos ?? [];
  const counts = videos.map((v) => placeCountFor(v.videoId));
  const allChecked = videos.length > 0 && counts.every((c) => c !== undefined);
  const withPlaces = counts.filter((c) => (c ?? 0) > 0).length;

  return (
    <section>
      <div className="flex items-baseline gap-2.5 mb-2.5">
        <span className="font-sans text-[10.5px] font-bold tracking-[0.2em] uppercase text-ink">
          {category.label}
        </span>
        <span className="flex-1 border-b border-rule" />
        <span className="font-serif italic text-[12px] text-ink-faint shrink-0">
          {videos.length} {videos.length === 1 ? "video" : "videos"}
          {allChecked && ` · ${withPlaces} with places`}
        </span>
      </div>

      {!category.videos?.length ? (
        <p className="text-xs text-ink-muted italic px-1">
          Nothing trending right now.
        </p>
      ) : (
        <>
          {/* A horizontal row on both sides, per the artboards. The negative
              margin and matching padding equal the panel gutter (22px on
              the mobile sheet, 20px at lg) so the row bleeds to the panel
              edge; no scroll-snap, which would scroll that gutter away. */}
          <div className="flex gap-2.5 overflow-x-auto pb-1.5 -mx-[22px] px-[22px] lg:-mx-5 lg:px-5">
            {category.videos?.map((video) => (
              <VideoThumbCard
                key={video.videoId}
                video={video}
                placeCount={placeCountFor(video.videoId)}
                isExpanded={video.videoId === expandedVideoId}
                onToggle={() => onToggleVideo(video)}
              />
            ))}
          </div>
          {expandedInThisCategory && (
            <div className="mt-2.5">
              <ExpandedVideo
                video={expandedInThisCategory}
                extraction={
                  extractionsByVideoId[expandedInThisCategory.videoId] ?? {
                    status: "idle",
                  }
                }
                onAddPlace={(place, targetDate) =>
                  onAddPlace({ place, video: expandedInThisCategory, targetDate })
                }
                onClose={() => onToggleVideo(expandedInThisCategory)}
                addedKeys={addedKeys}
                addingKeys={addingKeys}
                days={days}
                activeDayNumber={activeDayNumber}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

// The badge line under the channel: "3 places", "1 place", or "watch only"
// for a video that names none (still worth watching, so the card stays at
// full strength). The line is reserved before the check lands so rows do
// not jump when badges appear.
const placeBadge = (count: number | undefined): string => {
  if (count === undefined) return "";
  if (count === 0) return "watch only";
  return `${count} ${count === 1 ? "place" : "places"}`;
};

const VideoThumbCard = ({
  video,
  placeCount,
  isExpanded,
  onToggle,
}: {
  video: InspirationVideo;
  placeCount?: number;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`shrink-0 w-[150px] text-left group border-b-2 pb-1 transition-colors ${
        isExpanded ? "border-accent" : "border-transparent hover:border-rule"
      }`}
    >
      <div className="h-[84px] bg-map relative overflow-hidden">
        {video.thumbnail && (
          <img
            src={video.thumbnail}
            alt={video.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>
      <div className="pt-2">
        <div className="font-serif text-sm leading-[1.3] text-ink line-clamp-2">
          {video.title}
        </div>
        <div className="hand text-[15px] text-accent-text mt-0.5 truncate -rotate-1 origin-left">
          {video.channelTitle}
        </div>
        <div
          className="mt-1 min-h-[12px] font-sans text-[9.5px] font-bold leading-[normal] tracking-[0.14em] uppercase text-ink-faint"
          data-place-badge={placeCount === undefined ? "unknown" : String(placeCount)}
        >
          {placeBadge(placeCount)}
        </div>
      </div>
    </button>
  );
};

const ExpandedVideo = ({
  video,
  extraction,
  onAddPlace,
  onClose,
  addedKeys,
  addingKeys,
  days,
  activeDayNumber,
}: {
  video: InspirationVideo;
  extraction: ExtractionState;
  onAddPlace: (place: ExtractedPlace, targetDate: string | null) => void;
  onClose: () => void;
  addedKeys: Set<string>;
  addingKeys: Record<string, "pending" | "failed">;
  days: InspirationDayOption[];
  activeDayNumber?: number;
}) => {
  // When the expanded card mounts (or the user clicks a different video
  // while one is already open), scroll the Inspiration tab's scroll
  // container so this card lands at the top. We do this manually rather
  // than via scrollIntoView because the latter scrolls ALL ancestor
  // scrollables, which on desktop can shift the whole trip page layout
  // and make the right column look "cut off".
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const container = el.closest(
      '[data-inspiration-scroll="true"]'
    ) as HTMLElement | null;
    if (!container) return;
    // offsetTop is relative to the nearest positioned ancestor; the
    // scroll container isn't positioned, so we compute the delta via
    // getBoundingClientRect for robustness across nesting depths.
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const targetScrollTop =
      container.scrollTop + (elRect.top - containerRect.top) - 8;
    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  }, [video.videoId]);

  return (
    <div
      ref={rootRef}
      className="border border-rule bg-white overflow-hidden scroll-mt-2"
    >
      {/* The player leads; the close button rides on it as the artboard's
          30px paper disc rather than in a header row above. */}
      <div className="relative aspect-video bg-black w-full max-w-full overflow-hidden">
        {/* Before the iframe in DOM order so keyboard users reach it without
            tabbing through the whole player; it is positioned, so it still
            paints above the static iframe. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Collapse video"
          className="absolute top-2 right-2 h-[30px] w-[30px] rounded-full bg-[color-mix(in_srgb,#faf9f6_92%,transparent)] text-on-media inline-flex items-center justify-center"
        >
          <X className="h-[13px] w-[13px]" />
        </button>
        <iframe
          src={youtubeEmbedUrl(video.videoId)}
          title={video.title}
          className="w-full h-full block"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      <div className="px-3.5 pt-3">
        <div className="font-serif text-[15px] leading-[1.3] text-ink mb-0.5">
          {video.title}
        </div>
      </div>
      <ExtractedPlacesList
        extraction={extraction}
        video={video}
        onAddPlace={onAddPlace}
        addedKeys={addedKeys}
        addingKeys={addingKeys}
        days={days}
        activeDayNumber={activeDayNumber}
      />
    </div>
  );
};

const ExtractedPlacesList = ({
  extraction,
  video,
  onAddPlace,
  addedKeys,
  addingKeys,
  days,
  activeDayNumber,
}: {
  extraction: ExtractionState;
  video: InspirationVideo;
  onAddPlace: (place: ExtractedPlace, targetDate: string | null) => void;
  addedKeys: Set<string>;
  addingKeys: Record<string, "pending" | "failed">;
  days: InspirationDayOption[];
  activeDayNumber?: number;
}) => {
  if (extraction.status === "idle") {
    return null;
  }
  if (extraction.status === "loading") {
    return (
      <div className="p-3 flex items-center gap-2 text-xs text-ink-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Reading the video for places to add...
      </div>
    );
  }
  if (extraction.status === "error") {
    return (
      <div className="p-3 flex items-start gap-2 text-xs text-ink-muted">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
        <span>{extraction.message}</span>
      </div>
    );
  }
  if (!extraction.places?.length) {
    return (
      <div className="p-3 text-xs text-ink-muted">
        No specific places to add from this one.{" "}
        <a
          href={youtubeWatchUrl(video.videoId)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent-text hover:underline inline-flex items-center gap-1"
        >
          Watch on YouTube
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }
  return (
    <div className="px-3.5 pb-1">
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-ink-faint mt-2.5 mb-0.5">
        Places in this video · {extraction.places.length}
      </div>
      <ul>
        {extraction.places?.map((place, i) => (
          <PlaceRow
            key={`${place.name}-${i}`}
            place={place}
            video={video}
            onAddPlace={onAddPlace}
            addedKeys={addedKeys}
            addingKeys={addingKeys}
            days={days}
            activeDayNumber={activeDayNumber}
          />
        ))}
      </ul>
    </div>
  );
};

// One extracted place. Four states: idle (the "Add to Day N" pill plus a
// day-picker chevron), pending, failed, added. The day the row will target
// is chosen here and stays put across a Retry, so re-running the check
// lands the place where the user originally pointed it.
const PlaceRow = ({
  place,
  video,
  onAddPlace,
  addedKeys,
  addingKeys,
  days,
  activeDayNumber,
}: {
  place: ExtractedPlace;
  video: InspirationVideo;
  onAddPlace: (place: ExtractedPlace, targetDate: string | null) => void;
  addedKeys: Set<string>;
  addingKeys: Record<string, "pending" | "failed">;
  days: InspirationDayOption[];
  activeDayNumber?: number;
}) => {
  const addKey = makeAddKey(video.videoId, place.name);
  const isAdded = addedKeys.has(addKey);
  const addState = addingKeys[addKey];

  // Default target: the day the user is reading, else the first day.
  const defaultDay =
    days.find((d) => d.n === activeDayNumber) ?? days[0] ?? null;
  const [chosenDayId, setChosenDayId] = useState<string | null>(null);
  const chosenDay =
    days.find((d) => d.id === chosenDayId) ?? defaultDay;
  const targetDate = chosenDay?.date ?? null;

  return (
    <li className="relative flex items-center gap-2.5 py-2 lg:py-2.5 border-t border-rule-hair">
      <div className="flex-1 min-w-0">
        <div className="font-serif text-[15px] leading-tight truncate">
          {place.name}
        </div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-ink-faint mt-0.5">
          {place.type}
        </div>
      </div>

      {addState === "pending" ? (
        /* Same height as the pill it replaces: 34px desktop, 44 mobile. */
        <span className="shrink-0 inline-flex items-center gap-2 h-11 lg:h-[34px] kicker tracking-[0.12em] text-ink-muted whitespace-nowrap">
          <svg
            viewBox="0 0 40 12"
            fill="none"
            aria-hidden="true"
            className="w-10 h-3 shrink-0"
          >
            <path
              d="M2 8 C12 2, 26 10, 38 4"
              stroke="var(--fn-accent)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="2 7"
              className="animate-dot-walk"
            />
          </svg>
          Checking Google Places
        </span>
      ) : addState === "failed" ? (
        <span className="shrink-0 inline-flex items-center gap-2.5">
          <span className="kicker tracking-[0.12em] inline-flex items-center gap-[5px] text-danger whitespace-nowrap">
            <AlertCircle className="h-3 w-3 shrink-0" />
            Couldn't verify
          </span>
          <button
            type="button"
            onClick={() => onAddPlace(place, targetDate)}
            className="rule-link kicker tracking-[0.12em] text-ink hover:text-accent-text transition-colors lg:h-[34px] lg:[background-position:0_24px]"
          >
            Retry
          </button>
        </span>
      ) : isAdded ? (
        <span className="shrink-0 inline-flex items-center gap-2 px-1 py-2 kicker tracking-[0.12em] text-accent-text">
          {/* Hand-drawn tick: a square box with the stroke overshooting it. */}
          <span
            className="relative w-3.5 h-3.5 border-[1.5px] border-accent shrink-0"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              className="absolute -top-[3px] -left-[2px] w-[18px] h-[18px]"
            >
              <path
                d="M2 9 C5 12, 6 13, 7 11 C9 6, 11 3, 15 1"
                stroke="var(--fn-accent)"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </span>
          Added
        </span>
      ) : days.length > 0 ? (
        <span className="shrink-0 inline-flex rounded-full border border-rule-dash-2 overflow-hidden">
          <button
            type="button"
            onClick={() => onAddPlace(place, targetDate)}
            className="h-11 lg:h-[34px] pl-3 pr-1.5 kicker tracking-[0.12em] text-ink-muted hover:text-ink transition-colors whitespace-nowrap"
          >
            Add to Day {chosenDay?.n ?? 1}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Choose a day"
                className="h-11 lg:h-[34px] px-2.5 lg:px-2 inline-flex items-center border-l border-rule-dash-2 text-ink-muted hover:text-ink transition-colors"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            {/* The picker only changes the target; adding stays on the
                pill, so one tap never fires an unverified add. */}
            <DropdownMenuContent
              align="end"
              sideOffset={6}
              className="w-[170px] lg:w-[150px] rounded-none border-rule bg-white p-0 py-1.5 shadow-[0_18px_44px_-18px_rgba(var(--fn-shadow-rgb),0.3)]"
            >
              {days.map((d) => (
                <DropdownMenuItem
                  key={d.id}
                  onSelect={() => setChosenDayId(d.id)}
                  className={`flex items-baseline gap-2 rounded-none px-3 py-2 text-[12.5px] text-ink cursor-pointer focus:bg-paper-warm ${
                    d.id === chosenDay?.id ? "bg-paper-warm" : ""
                  }`}
                >
                  <span className="font-serif italic text-ink-faint w-4">
                    {d.n}
                  </span>
                  <span className="truncate">{d.title}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      ) : (
        // No days yet: a plain Add; the concierge places it.
        <button
          type="button"
          onClick={() => onAddPlace(place, null)}
          className="shrink-0 h-11 lg:h-[34px] px-3 rounded-full border border-rule-dash-2 kicker tracking-[0.12em] text-ink-muted hover:text-ink transition-colors"
        >
          Add
        </button>
      )}
    </li>
  );
};

export default Inspiration;
